import Box from '@material-ui/core/Box'
import clsx from 'clsx'
import React, { PropsWithChildren, useEffect, useState } from 'react'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import InProgress from './InProgress'
import ErrorModal from './Modals/ErrorModal.view'
import ExitModal from './Modals/ExitModal.view'
import TimeoutModal from './Modals/TimeoutModal.view'
import Opened from './Opened/Opened.view'
import useStyles from './OpeningDrop.style'
import { OpeningDropProps } from './OpeningDrop.types'
import IconRarum from 'components/IconRarum'
import Text from 'openspace/components/Text'
import { LPTypo } from 'containers/OneUIProvider/OneUIProvider.data'
import Spacing from '@onepercentio/one-ui/components/Spacing'
import Button from '@onepercentio/one-ui/components/Button'
import { t } from 'translate/i18n'
import Container from 'openspace/components/Container'
import Styles from './OpeningDrop.module.scss'
import { ContractType } from 'core/logic/contract/types/types'

const PACK_OPENING_TIMEOUT = 1000 * 60
const OpeningDrop: React.FC<OpeningDropProps> = (props) => {
  const { main, pulse } = useStyles()
  const rootNodeRef = React.useRef(null)
  const { isDropOpen } = props

  return (
    <main className={main}>
      <Box className={pulse}>
        <SwitchTransition>
          <CSSTransition
            nodeRef={rootNodeRef}
            key={isDropOpen ? 'drop-opened' : 'drop-opening'}
            classNames='long-fade'
            timeout={1200}>
            <div
              ref={rootNodeRef}
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}>
              <BackgroundEffect />
              <OpeningDropContent {...props} />
            </div>
          </CSSTransition>
        </SwitchTransition>
      </Box>
    </main>
  )
}

export function OpeningDropContent(props: OpeningDropProps) {
  const { drop, assets, isDropOpen, delivery, onGoToTracking } = props
  const { onBackIntent, onBack, onStay } = props
  const [openingTimeout, setOpeningTimeout] = useState(false)
  const { messages, messageIndex } = props
  const { backModalState, errorModal } = props
  const mode = props.delivery
    ? props.delivery?.status === 'CLAIMED'
      ? 'claim'
      : 'purchase'
    : undefined

  const isDigitalTwinDelivery =
    delivery?.contractType === ContractType.DigitalTwin

  /** Sets a warning for when the opening flow takes too long */
  useEffect(() => {
    if (isDropOpen || isDigitalTwinDelivery) return
    const timeout = setTimeout(() => {
      setOpeningTimeout(true)
    }, PACK_OPENING_TIMEOUT)

    return () => {
      clearTimeout(timeout)
    }
  }, [isDropOpen, isDigitalTwinDelivery])

  const contentToDisplay = (() => {
    if (isDropOpen) {
      return (
        <Opened
          mode={mode}
          packTitle={drop?.title || ''}
          assets={assets}
          onBackIntent={onBackIntent}
        />
      )
    } else {
      if (isDigitalTwinDelivery)
        return (
          <WaitingDelivery
            onBackToAssets={onBackIntent}
            onGoToTracking={onGoToTracking}
          />
        )
      else
        return (
          <InProgress
            mode={mode}
            drop={drop}
            onBackIntent={onBackIntent}
            messages={messages}
            messageIndex={messageIndex}
            deliveryCover={props.delivery?.coverImage || (drop?.media.icon ?? drop?.image!)}
          />
        )
    }
  })()

  return (
    <>
      {contentToDisplay}
      <ExitModal open={backModalState} onStay={onStay} onLeave={onBack} />
      <ErrorModal open={errorModal} onLeave={onBack} />
      <TimeoutModal
        open={openingTimeout}
        onStay={() => setOpeningTimeout(false)}
        onLeave={onBack}
      />
    </>
  )
}

function BackgroundEffect() {
  const { light, x1, x2, x3, x4, x5, x6, x7, x8, x9 } = useStyles()
  return (
    <>
      <div className={clsx(light, x1)}></div>
      <div className={clsx(light, x2)}></div>
      <div className={clsx(light, x3)}></div>
      <div className={clsx(light, x4)}></div>
      <div className={clsx(light, x5)}></div>
      <div className={clsx(light, x6)}></div>
      <div className={clsx(light, x7)}></div>
      <div className={clsx(light, x8)}></div>
      <div className={clsx(light, x9)}></div>
    </>
  )
}

export function WaitingDelivery({
  onGoToTracking,
  onBackToAssets,
}: PropsWithChildren<ClickActions<'onBackToAssets' | 'onGoToTracking'>>) {
  return (
    <>
      <Container stretch center textCenter className={Styles.waiting}>
        <IconRarum size={120} color='var(--ok-color)' icon='roundCheckmark' />
        <Spacing size='16' />
        <Text
          code='authenticated.openingDrop.waitingDelivery.title'
          type={LPTypo.H5}
        />
        <Spacing size='32' />
        <Text
          code='authenticated.openingDrop.waitingDelivery.description'
          type={LPTypo.H3}
          className={Styles.description}
        />
        <Spacing size='40' />
        <Text
          code='authenticated.openingDrop.waitingDelivery.disclaimer'
          type={LPTypo.P2}
          className={Styles.disclaimer}
        />
        <Spacing size='stretch' />
        <Button variant='filled' color='primary' onClick={onGoToTracking}>
          {t('authenticated.openingDrop.waitingDelivery.actions.track')}
        </Button>
        <Spacing size='8' />
        <Button variant='outline' onClick={onBackToAssets}>
          {t('generic.goBack')}
        </Button>
      </Container>
    </>
  )
}

export default OpeningDrop
